// ============================================================================
// CRM NOTES — timeline on a booking's CRM record: notes people write (author
// and time recorded, editable/deletable by the author, deletable by system
// admins) and activity entries the backend logs when CRM data changes.
// ============================================================================

import React, { useCallback, useEffect, useState } from 'react';
import {
  Loader, Pencil, Trash2, Check, X, Link2, Unlink, Building2, Ban, RotateCcw, RefreshCw, Send, Mail
} from 'lucide-react';
import { toast } from 'sonner';
import {
  CrmBooking,
  CrmNote,
  CrmActivityType,
  CrmNotesSummary,
  MAX_NOTE_LENGTH,
  listNotes,
  addNote,
  editNote,
  deleteNote,
  effectiveValues
} from '../../services/studio-crm';

interface Props {
  booking: CrmBooking;
  currentUser: { userId: string; canModerateNotes: boolean } | null;
  onSummary: (summary: CrmNotesSummary) => void;
}

const ACTIVITY_ICONS: Record<CrmActivityType, React.ElementType> = {
  opportunity_linked: Link2,
  opportunity_updated: RefreshCw,
  opportunity_unlinked: Unlink,
  account_set: Building2,
  account_cleared: Building2,
  excluded: Ban,
  included: RotateCcw,
  email_sent: Mail
};

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.round(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hours = Math.round(min / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} d ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function absoluteTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
}

function summarise(notes: CrmNote[]): CrmNotesSummary {
  const human = notes.filter(n => n.kind === 'note');
  return { count: human.length, lastNoteAt: human[0]?.createdAt || null };
}

export default function CrmNotes({ booking, currentUser, onSummary }: Props) {
  const [notes, setNotes] = useState<CrmNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [tag, setTag] = useState('');
  const [posting, setPosting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState('');

  const oppNames = new Map(booking.crm.opportunities.map(o => [o.opportunityId, effectiveValues(o).name]));

  // Refetch whenever the booking's CRM data changes, so the activity entry the
  // backend just logged appears. The notes read is strongly consistent.
  const crmVersion = [
    booking.bookingId,
    ...booking.crm.opportunities.map(o => `${o.opportunityId}:${o.updatedAt}`),
    booking.crm.account?.updatedAt || '',
    booking.crm.exclusion?.excludedAt || ''
  ].join('|');

  const apply = useCallback((next: CrmNote[]) => {
    setNotes(next);
    onSummary(summarise(next));
  }, [onSummary]);

  useEffect(() => {
    let cancelled = false;
    listNotes(booking.bookingId)
      .then(result => { if (!cancelled) apply(result); })
      .catch(() => { if (!cancelled) toast.error('Failed to load notes'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crmVersion]);

  async function submit() {
    if (!body.trim() || posting) return;
    setPosting(true);
    try {
      const note = await addNote(booking.bookingId, { body: body.trim(), ...(tag ? { opportunityId: tag } : {}) });
      apply([note, ...notes]);
      setBody('');
      setTag('');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to add note');
    } finally {
      setPosting(false);
    }
  }

  async function saveEdit(note: CrmNote) {
    if (!editBody.trim()) return;
    try {
      const updated = await editNote(booking.bookingId, note.noteId, editBody.trim());
      apply(notes.map(n => (n.noteId === note.noteId ? updated : n)));
      setEditingId(null);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to edit note');
    }
  }

  async function remove(note: CrmNote) {
    if (!window.confirm('Delete this note?')) return;
    try {
      await deleteNote(booking.bookingId, note.noteId);
      apply(notes.filter(n => n.noteId !== note.noteId));
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to delete note');
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Composer */}
      <div className="bg-white border border-[#e2e8f0] rounded-xl p-3 flex flex-col gap-2">
        <textarea value={body} onChange={e => setBody(e.target.value)} rows={3} maxLength={MAX_NOTE_LENGTH}
          placeholder="Add a note…"
          onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(); }}
          className="w-full border border-[#e2e8f0] rounded-lg px-3 py-2 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none" />
        <div className="flex items-center gap-2">
          {booking.crm.opportunities.length > 0 && (
            <select value={tag} onChange={e => setTag(e.target.value)}
              className="h-8 max-w-[220px] rounded-lg border border-[#e2e8f0] bg-white px-2 text-xs text-navy focus:outline-none focus:ring-2 focus:ring-primary/30">
              <option value="">About: whole booking</option>
              {booking.crm.opportunities.map(o => (
                <option key={o.opportunityId} value={o.opportunityId}>About: {effectiveValues(o).name}</option>
              ))}
            </select>
          )}
          <span className="ml-auto text-[11px] text-slate-400">{body.length}/{MAX_NOTE_LENGTH}</span>
          <button onClick={submit} disabled={!body.trim() || posting} title="Add note (Ctrl/Cmd+Enter)"
            className="h-8 px-3 rounded-lg bg-primary hover:bg-primary-dark text-white text-xs font-bold flex items-center gap-1 disabled:opacity-50 transition-colors">
            {posting ? <Loader size={12} className="animate-spin" /> : <Send size={12} />} Add note
          </button>
        </div>
      </div>

      {/* Timeline */}
      {loading ? (
        <div className="flex justify-center py-4 text-slate-400"><Loader size={16} className="animate-spin" /></div>
      ) : notes.length === 0 ? (
        <p className="text-xs text-slate-400 px-1">No notes or activity yet.</p>
      ) : (
        <ol className="flex flex-col gap-2">
          {notes.map(note => {
            if (note.kind === 'activity' && note.activity) {
              const Icon = ACTIVITY_ICONS[note.activity.type] || RefreshCw;
              return (
                <li key={note.noteId} className="flex items-start gap-2 px-1 text-xs text-slate-500">
                  <Icon size={12} className="mt-0.5 text-slate-400 flex-shrink-0" />
                  <span className="min-w-0 break-words">
                    {note.activity.summary}
                    <span className="text-slate-400"> · {note.authorName} · <span title={absoluteTime(note.createdAt)}>{relativeTime(note.createdAt)}</span></span>
                  </span>
                </li>
              );
            }
            const isAuthor = currentUser?.userId === note.authorId;
            const canDelete = isAuthor || !!currentUser?.canModerateNotes;
            const editing = editingId === note.noteId;
            return (
              <li key={note.noteId} className="bg-white border border-[#e2e8f0] rounded-xl px-3 py-2.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="text-xs text-slate-500 min-w-0">
                    <span className="font-semibold text-navy">{note.authorName}</span>
                    {' · '}<span title={absoluteTime(note.createdAt)}>{relativeTime(note.createdAt)}</span>
                    {note.editedAt && <span title={`Edited ${absoluteTime(note.editedAt)}`}> · edited</span>}
                  </div>
                  {!editing && (isAuthor || canDelete) && (
                    <div className="flex items-center flex-shrink-0 -mt-1 -mr-1">
                      {isAuthor && (
                        <button onClick={() => { setEditingId(note.noteId); setEditBody(note.body || ''); }} title="Edit"
                          className="p-1 rounded-lg hover:bg-slate-100 text-slate-400"><Pencil size={12} /></button>
                      )}
                      {canDelete && (
                        <button onClick={() => remove(note)} title="Delete"
                          className="p-1 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600"><Trash2 size={12} /></button>
                      )}
                    </div>
                  )}
                </div>
                {note.opportunityId && (
                  <span className="inline-block mt-1 text-[11px] font-semibold px-1.5 rounded bg-primary/10 text-primary">
                    {oppNames.get(note.opportunityId) || 'Unlinked opportunity'}
                  </span>
                )}
                {editing ? (
                  <div className="mt-2 flex flex-col gap-2">
                    <textarea value={editBody} onChange={e => setEditBody(e.target.value)} rows={3} maxLength={MAX_NOTE_LENGTH} autoFocus
                      onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) saveEdit(note); }}
                      className="w-full border border-[#e2e8f0] rounded-lg px-3 py-2 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none" />
                    <div className="flex justify-end gap-2">
                      <button onClick={() => setEditingId(null)}
                        className="h-7 px-2.5 rounded-lg border border-slate-200 bg-white text-slate-600 text-xs font-bold flex items-center gap-1">
                        <X size={12} /> Cancel
                      </button>
                      <button onClick={() => saveEdit(note)} disabled={!editBody.trim()}
                        className="h-7 px-2.5 rounded-lg bg-primary text-white text-xs font-bold flex items-center gap-1 disabled:opacity-50">
                        <Check size={12} /> Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="mt-1 text-sm text-navy whitespace-pre-wrap break-words">{note.body}</p>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
